import { useEffect, useState } from 'react';

import * as T from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import { Box, Button, Flex } from '@olist/united';

import { Step } from '../../../components';
import useResponsiveFirstSteps from '../../../hooks/use-responsive-first-steps';
import { stepNames } from '../constants';
import {
  BankAccountStepContent,
  LogisticAddressStepContent,
  OutboundSubscriptionWithNoPlanSelected,
  SubscriptionStepContent,
} from '../step-contents';
import { StepContent, StepPanel } from './onboarding-steps.style';

const SORTED_STEPS = [stepNames.LOGISTIC_ADDRESS, stepNames.BANK_ACCOUNT, stepNames.SUBSCRIPTION];

const QualificationCompleted = () => {
  const { t } = useTranslation();

  return <Step title={t`you completed the requirements`} isCompleted />;
};

const OnboardingSteps = ({
  completedSteps,
  isAllStepsCompleted,
  isLoading,
  onCardClick,
  onChangeStepContent,
  shouldHideSubscriptionStep,
  shouldShowChoosePlanFromInvite,
  shouldShowGenericChosePlan,
}) => {
  const { t } = useTranslation();

  const [activeStep, setActiveStep] = useState(undefined);

  const { isMobile, isMobileStepActive, setIsMobileStepActive, shouldHiddenOnMobile } =
    useResponsiveFirstSteps();

  useEffect(() => {
    if (!isLoading) {
      const uncompletedSteps = isAllStepsCompleted
        ? []
        : SORTED_STEPS.filter((step) => !completedSteps.includes(step));

      if (shouldShowGenericChosePlan) {
        return setActiveStep(uncompletedSteps[0]);
      }
      const shouldHideActiveStepsWithNoInvite =
        uncompletedSteps[0] === stepNames.SUBSCRIPTION && !shouldShowChoosePlanFromInvite;

      if (shouldHideActiveStepsWithNoInvite) {
        return setActiveStep(undefined);
      }
      setActiveStep(uncompletedSteps[0]);
    }
    return () => {
      setActiveStep(undefined);
    };
  }, [
    completedSteps,
    isAllStepsCompleted,
    isLoading,
    shouldShowGenericChosePlan,
    shouldShowChoosePlanFromInvite,
  ]);

  useEffect(() => {
    onChangeStepContent(activeStep);
  }, [activeStep, onChangeStepContent]);

  const isLogisticAddressStepActive = activeStep === stepNames.LOGISTIC_ADDRESS;
  const isBankAccountStepActive = activeStep === stepNames.BANK_ACCOUNT;
  const isSubscriptionStepActive =
    activeStep === stepNames.SUBSCRIPTION && !shouldHideSubscriptionStep;

  const isFromInviteWithNoPlan = !activeStep && !shouldShowChoosePlanFromInvite;

  const handleOnCardClick = (stepName) => () => {
    setActiveStep(stepName);
    onCardClick(stepName);

    if (isMobile) {
      setIsMobileStepActive(true);
    }
  };

  const cards = [
    {
      isActive: isLogisticAddressStepActive,
      isLoading,
      isCompleted: isAllStepsCompleted || completedSteps.includes(stepNames.LOGISTIC_ADDRESS),
      title: t`register your logistical information`,
      onClick: handleOnCardClick(stepNames.LOGISTIC_ADDRESS),
    },
    {
      isActive: isBankAccountStepActive,
      isLoading,
      isCompleted: isAllStepsCompleted || completedSteps.includes(stepNames.BANK_ACCOUNT),
      title: t`get paid for your sales`,
      onClick: handleOnCardClick(stepNames.BANK_ACCOUNT),
    },
  ];

  const baseCardPlans = {
    isActive: isSubscriptionStepActive,
    isLoading,
    isCompleted: isAllStepsCompleted || completedSteps.includes(stepNames.SUBSCRIPTION),
    onClick: handleOnCardClick(stepNames.SUBSCRIPTION),
  };

  if (!shouldHideSubscriptionStep) {
    if (shouldShowGenericChosePlan) {
      cards.push({
        ...baseCardPlans,
        title: t`choose the ideal subscription plan`,
      });
    } else if (shouldShowChoosePlanFromInvite) {
      cards.push({
        ...baseCardPlans,
        title: t`plan subscription`,
      });
    }
  }

  return (
    <Flex mb={8}>
      {!isMobileStepActive && (
        <StepPanel>
          {!shouldHiddenOnMobile && <QualificationCompleted />}
          <Step
            isCurrent
            isCompleted={isAllStepsCompleted}
            title={t`we are almost there!`}
            description={
              <Trans i18nKey="unlockSteps">
                Unlock the steps below to activate your sales operation with <b>olist store</b>.
              </Trans>
            }
            cards={cards}
          />
          {!shouldHiddenOnMobile && (
            <Step isCompleted={isAllStepsCompleted} isBlocked title={t`start selling`} />
          )}
        </StepPanel>
      )}

      {isMobileStepActive && (
        <StepContent
          mb="88px"
          flexDirection="column"
          display={['flex', 'flex', 'flex', 'flex', 'none']}
          data-testid="step-content-mobile"
        >
          <Box>
            <Button
              startIcon="chevron-left-outline"
              onClick={() => setIsMobileStepActive(false)}
            >{t`back`}</Button>
          </Box>
          {isLogisticAddressStepActive && <LogisticAddressStepContent />}
          {isBankAccountStepActive && <BankAccountStepContent />}
          {isSubscriptionStepActive && <SubscriptionStepContent />}
        </StepContent>
      )}

      <StepContent ml={[0, 0, 0, '5%', '112px']} display={['none', 'none', 'none', 'none', 'flex']}>
        {isLogisticAddressStepActive && <LogisticAddressStepContent />}
        {isBankAccountStepActive && <BankAccountStepContent />}
        {isSubscriptionStepActive && (
          <SubscriptionStepContent isFromInvite={shouldShowChoosePlanFromInvite} />
        )}
        {isFromInviteWithNoPlan && !isLoading && (
          <OutboundSubscriptionWithNoPlanSelected isFromInvite={shouldShowChoosePlanFromInvite} />
        )}
      </StepContent>
    </Flex>
  );
};

OnboardingSteps.propTypes = {
  completedSteps: T.arrayOf(T.oneOf(Object.values(stepNames))),
  isAllStepsCompleted: T.bool,
  isLoading: T.bool,
  shouldShowGenericChosePlan: T.bool,
  shouldShowChoosePlanFromInvite: T.bool,
  onCardClick: T.func,
  onChangeStepContent: T.func,
  shouldHideSubscriptionStep: T.bool,
};
OnboardingSteps.defaultProps = {
  completedSteps: [],
  isAllStepsCompleted: false,
  isLoading: false,
  shouldShowGenericChosePlan: false,
  shouldShowChoosePlanFromInvite: false,
  onCardClick: () => {},
  onChangeStepContent: () => {},
  shouldHideSubscriptionStep: false,
};

export default OnboardingSteps;
